import React, { createContext, useContext, useReducer, ReactNode, useState } from 'react';
import { useResponsive, getResponsiveWindowProps, getCascadePosition } from '@/hooks/use-responsive';
import { AppMenuConfig } from '@/types/MenuTypes';
import { usePreferences } from '@/contexts/PreferencesContext';

export interface WindowState {
  id: string;
  title: string;
  component: ReactNode;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  isActive: boolean;
  appType?: string;
  menuConfig?: AppMenuConfig;
  thumbnail?: string;
  lastThumbnailUpdate?: number;
}

interface WindowManagerState {
  windows: WindowState[];
  highestZIndex: number;
}

type WindowAction = 
  | { type: 'OPEN_WINDOW'; payload: Omit<WindowState, 'zIndex' | 'isActive'> }
  | { type: 'CLOSE_WINDOW'; payload: { id: string } }
  | { type: 'FOCUS_WINDOW'; payload: { id: string } }
  | { type: 'MINIMIZE_WINDOW'; payload: { id: string; thumbnail?: string } }
  | { type: 'MAXIMIZE_WINDOW'; payload: { id: string } }
  | { type: 'RESTORE_WINDOW'; payload: { id: string } }
  | { type: 'MOVE_WINDOW'; payload: { id: string; position: { x: number; y: number } } }
  | { type: 'RESIZE_WINDOW'; payload: { id: string; size: { width: number; height: number } } };

const initialState: WindowManagerState = {
  windows: [],
  highestZIndex: 100,
};

function windowReducer(state: WindowManagerState, action: WindowAction): WindowManagerState {
  switch (action.type) {
    case 'OPEN_WINDOW': {
      const existingWindow = state.windows.find(w => w.id === action.payload.id);
      if (existingWindow) {
        // Focus existing window
        return {
          ...state,
          highestZIndex: state.highestZIndex + 1,
          windows: state.windows.map(w => ({
            ...w,
            isActive: w.id === action.payload.id,
            zIndex: w.id === action.payload.id ? state.highestZIndex + 1 : w.zIndex,
            isMinimized: w.id === action.payload.id ? false : w.isMinimized,
          })),
        };
      }

      const newWindow: WindowState = {
        ...action.payload,
        zIndex: state.highestZIndex + 1,
        isActive: true,
      };

      return {
        ...state,
        highestZIndex: state.highestZIndex + 1,
        windows: [
          ...state.windows.map(w => ({ ...w, isActive: false })),
          newWindow,
        ],
      };
    }

    case 'CLOSE_WINDOW': {
      // Clear thumbnail cache when closing window
      const windowToClose = state.windows.find(w => w.id === action.payload.id);
      if (windowToClose?.thumbnail) {
        // Let the thumbnail cleanup happen automatically through cache expiry
      }
      return {
        ...state,
        windows: state.windows.filter(w => w.id !== action.payload.id),
      };
    }

    case 'FOCUS_WINDOW': {
      return {
        ...state,
        highestZIndex: state.highestZIndex + 1,
        windows: state.windows.map(w => ({
          ...w,
          isActive: w.id === action.payload.id,
          zIndex: w.id === action.payload.id ? state.highestZIndex + 1 : w.zIndex,
        })),
      };
    }

    case 'MINIMIZE_WINDOW': {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.payload.id
            ? { 
                ...w, 
                isMinimized: true, 
                isActive: false,
                thumbnail: action.payload.thumbnail || w.thumbnail,
                lastThumbnailUpdate: action.payload.thumbnail ? Date.now() : w.lastThumbnailUpdate
              }
            : w
        ),
      };
    }

    case 'MAXIMIZE_WINDOW': {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.payload.id
            ? { ...w, isMaximized: !w.isMaximized }
            : w
        ),
      };
    }

    case 'RESTORE_WINDOW': {
      return {
        ...state,
        highestZIndex: state.highestZIndex + 1,
        windows: state.windows.map(w =>
          w.id === action.payload.id
            ? { 
                ...w, 
                isMinimized: false, 
                isActive: true,
                zIndex: state.highestZIndex + 1,
              }
            : { ...w, isActive: false }
        ),
      };
    }

    case 'MOVE_WINDOW': {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.payload.id
            ? { ...w, position: action.payload.position }
            : w
        ),
      };
    }

    case 'RESIZE_WINDOW': {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.payload.id
            ? { ...w, size: action.payload.size }
            : w
        ),
      };
    }

    default:
      return state;
  }
}

interface WindowManagerContextType {
  windows: WindowState[];
  highestZIndex: number;
  activeWindowId?: string;
  activeWindow?: WindowState;
  isMobile: boolean;
  isTablet: boolean;
  screenDimensions: { width: number; height: number };
  openWindow: (id: string, title: string, component: ReactNode, options?: Partial<WindowState>) => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string, thumbnail?: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  moveWindow: (id: string, position: { x: number; y: number }) => void;
  resizeWindow: (id: string, size: { width: number; height: number }) => void;
  isWindowOpen: (id: string) => boolean;
}

const WindowManagerContext = createContext<WindowManagerContextType | null>(null);

export const useWindowManager = () => {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used within a WindowManagerProvider');
  }
  return context;
};

interface WindowManagerProviderProps {
  children: ReactNode;
}

export const WindowManagerProvider: React.FC<WindowManagerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(windowReducer, initialState);
  const { width, height, isMobile, isTablet } = useResponsive();
  const { windowCascade } = usePreferences();
  const [lastActiveWindowPosition, setLastActiveWindowPosition] = useState<{ x: number; y: number } | null>(null);

  const openWindow = (id: string, title: string, component: ReactNode, options?: Partial<WindowState>) => {
    const responsiveProps = getResponsiveWindowProps(width, height);
    
    // Determine if we should use cascade positioning
    const shouldCascade = 
      windowCascade.enabled && 
      width >= 1024 && // Desktop only
      !options?.isMaximized && 
      !responsiveProps.isMaximized;
    
    let windowPosition = options?.position || responsiveProps.position;
    
    // Calculate cascade position if enabled
    if (shouldCascade && !options?.position) {
      windowPosition = getCascadePosition({
        previousPosition: lastActiveWindowPosition,
        offset: windowCascade.offset,
        viewportWidth: width,
        viewportHeight: height,
        windowWidth: options?.size?.width || responsiveProps.size.width,
        windowHeight: options?.size?.height || responsiveProps.size.height,
        menuBarHeight: 28
      });
    }
    
    dispatch({
      type: 'OPEN_WINDOW',
      payload: {
        id,
        title,
        component,
        position: windowPosition,
        size: options?.size || responsiveProps.size,
        isMinimized: options?.isMinimized || false,
        isMaximized: options?.isMaximized || responsiveProps.isMaximized,
        appType: options?.appType,
        menuConfig: options?.menuConfig,
      },
    });
    
    // Update last active window position for next cascade
    if (!options?.isMaximized && !responsiveProps.isMaximized) {
      setLastActiveWindowPosition(windowPosition);
    }
  };

  const closeWindow = (id: string) => {
    dispatch({ type: 'CLOSE_WINDOW', payload: { id } });
  };

  const focusWindow = (id: string) => {
    dispatch({ type: 'FOCUS_WINDOW', payload: { id } });
    
    // Update last active window position when focusing a window
    const focusedWindow = state.windows.find(w => w.id === id);
    if (focusedWindow && !focusedWindow.isMaximized) {
      setLastActiveWindowPosition(focusedWindow.position);
    }
  };

  const minimizeWindow = (id: string, thumbnail?: string) => {
    dispatch({ type: 'MINIMIZE_WINDOW', payload: { id, thumbnail } });
  };

  const maximizeWindow = (id: string) => {
    dispatch({ type: 'MAXIMIZE_WINDOW', payload: { id } });
  };

  const moveWindow = (id: string, position: { x: number; y: number }) => {
    dispatch({ type: 'MOVE_WINDOW', payload: { id, position } });
  };

  const resizeWindow = (id: string, size: { width: number; height: number }) => {
    dispatch({ type: 'RESIZE_WINDOW', payload: { id, size } });
  };

  const restoreWindow = (id: string) => {
    dispatch({ type: 'RESTORE_WINDOW', payload: { id } });
  };

  const isWindowOpen = (id: string) => {
    return state.windows.some(w => w.id === id && !w.isMinimized);
  };

  const activeWindow = state.windows.find(w => w.isActive);

  const value = {
    windows: state.windows,
    highestZIndex: state.highestZIndex,
    activeWindowId: activeWindow?.id,
    activeWindow,
    isMobile,
    isTablet,
    screenDimensions: { width, height },
    openWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    moveWindow,
    resizeWindow,
    isWindowOpen,
  };


  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
};